import axios from "axios";

import type { MergeAmendedSearchCriteria, MergeIhiReviewOutcome } from "../../../api/healthcare/PatientApi";
import {
	REVIEW_REASON_MERGE_ALERT_LOCAL_ACTIVE_RETURNED_EXPIRED,
	REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_ACTIVE,
	REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_EXPIRED,
	REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_PROVISIONAL,
	REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_UNVERIFIED,
	REVIEW_REASON_MERGE_ALERT_VERIFIED_RETURNED_UNVERIFIED,
	REVIEW_REASON_MERGE_RESOLVED_REPLACEMENT_NOT_ACCEPTED,
	REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_ACTIVE,
	REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_DECEASED,
	REVIEW_REASON_MERGE_WARNING_LOCAL_ACTIVE_RETURNED_DECEASED,
} from "../../../types/admin/hiAlerts";

// CU-14ygtkj9tu1: maps a merge refusal to fixed wording by exact code match; backend message and body are never shown.

export const MERGE_CODE_NO_MATCH = "patient_merge_ihi_no_match";
export const MERGE_CODE_AMENDED_SEARCH_STALE = "patient_merge_amended_search_stale";

const AMENDED_SEARCH_TYPE_NUMBER = 3;

export const MERGE_AMENDED_SEARCH_FIELDS: ReadonlyArray<keyof MergeAmendedSearchCriteria> = [
	"medicare_card_number",
	"given_name",
	"family_name",
	"date_of_birth",
	"sex",
];

export interface MergeAmendedSearchOffer {
	amendsMergeId: number;
}

export type MergeOutcomeKind = "blocked" | "amendment_stale" | "amendment_invalid" | "unconfirmed" | "unknown";

export interface MergeOutcome {
	kind: MergeOutcomeKind;
	severity: "error" | "warning";
	title: string;
	detail: string;
	seeHiReviews: boolean;
	amendedSearch: MergeAmendedSearchOffer | null;
	ihiReviews?: MergeIhiReviewOutcome[];
}

export type MergeAttemptResult =
	| { kind: "started"; mergeId: number; ihiReviews?: MergeIhiReviewOutcome[] }
	| { kind: "outcome"; outcome: MergeOutcome }
	| { kind: "handled" };

const NOT_MOVED = "No records were moved.";

const outcome = (
	kind: MergeOutcomeKind,
	severity: MergeOutcome["severity"],
	title: string,
	detail: string,
	seeHiReviews = false,
	amendedSearch: MergeAmendedSearchOffer | null = null
): MergeOutcome => ({ kind, severity, title, detail, seeHiReviews, amendedSearch });

const blocked = (title: string, detail: string, seeHiReviews = false): MergeOutcome =>
	outcome("blocked", "error", title, `${detail} ${NOT_MOVED}`, seeHiReviews);

const NOT_CONFIRMED = blocked(
	"Merge blocked — IHI not confirmed",
	"The IHI for the merged record could not be confirmed as active and verified with the HI Service."
);

export const UNKNOWN_MERGE_OUTCOME = outcome(
	"unknown",
	"error",
	"Merge outcome not recognised",
	"The response did not confirm a completed merge. Do not assume the records were merged — close this window, refresh the patient list and check both records before trying again."
);

const UNCONFIRMED_AMENDMENT = outcome(
	"unconfirmed",
	"error",
	"Amended search not completed",
	"The amended search could not be completed and the merge was not confirmed. Do not assume the records were merged — close this window, refresh the patient list and start the merge again if needed."
);

const STALE_AMENDMENT = outcome(
	"amendment_stale",
	"warning",
	"Amended search no longer valid",
	`This amended search no longer follows the latest merge attempt for these records. ${NOT_MOVED} Close this window and start the merge again.`
);

const INVALID_AMENDMENT = outcome(
	"amendment_invalid",
	"warning",
	"Amended details not accepted",
	"The amended details do not meet the Medicare IHI search requirements. Check each field and resubmit."
);

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isPositiveInteger = (value: unknown): value is number => Number.isInteger(value) && (value as number) > 0;

// CU-14ygtkj9tu0: an offer is honoured only when it names exactly the five Search Type 3 fields; anything else offers no amendment.
export const parseAmendedSearchOffer = (value: unknown): MergeAmendedSearchOffer | null => {
	if (!isRecord(value) || value.available !== true || value.search_type_number !== AMENDED_SEARCH_TYPE_NUMBER) {
		return null;
	}
	const { amends_merge_id: amendsMergeId, criteria } = value;
	if (!isPositiveInteger(amendsMergeId) || !Array.isArray(criteria) || criteria.length !== MERGE_AMENDED_SEARCH_FIELDS.length) {
		return null;
	}
	const offered = new Set(criteria);
	return offered.size === MERGE_AMENDED_SEARCH_FIELDS.length && MERGE_AMENDED_SEARCH_FIELDS.every((field) => offered.has(field))
		? { amendsMergeId }
		: null;
};

const UC035_MERGE_REVIEW_REASONS: ReadonlySet<string> = new Set([
	REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_PROVISIONAL,
	REVIEW_REASON_MERGE_ALERT_UNSUPPORTED_UNVERIFIED,
	REVIEW_REASON_MERGE_ALERT_VERIFIED_RETURNED_UNVERIFIED,
	REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_ACTIVE,
	REVIEW_REASON_MERGE_STORED_RETIRED_LOCAL_DECEASED,
	REVIEW_REASON_MERGE_WARNING_LOCAL_ACTIVE_RETURNED_DECEASED,
	REVIEW_REASON_MERGE_ALERT_LOCAL_ACTIVE_RETURNED_EXPIRED,
	REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_ACTIVE,
	REVIEW_REASON_MERGE_ALERT_LOCAL_DECEASED_RETURNED_EXPIRED,
	REVIEW_REASON_MERGE_RESOLVED_REPLACEMENT_NOT_ACCEPTED,
]);

const UC035_REQUIREMENT_ID = /^HI_035_\d{6}$/;

// CU-14ygtkjc8yy: only known UC.035 merge reasons with a UC.035 anchor are shown, once each; anything else in the body is ignored.
export const parseIhiReviewOutcomes = (value: unknown): MergeIhiReviewOutcome[] => {
	if (!Array.isArray(value)) {
		return [];
	}
	const byReason = new Map<string, MergeIhiReviewOutcome>();
	for (const item of value) {
		if (
			isRecord(item) &&
			typeof item.reason === "string" &&
			UC035_MERGE_REVIEW_REASONS.has(item.reason) &&
			typeof item.requirement_id === "string" &&
			UC035_REQUIREMENT_ID.test(item.requirement_id) &&
			typeof item.severity === "string" &&
			!byReason.has(item.reason)
		) {
			byReason.set(item.reason, { reason: item.reason, requirement_id: item.requirement_id, severity: item.severity });
		}
	}
	return [...byReason.values()];
};

// CU-14ygtkjc8yy: a 200 partial_failure naming its merge already moved the records, so it is a merge result, not a request failure.
export const isMergedWithPartialFailure = (response: unknown): boolean =>
	isRecord(response) && response.success === false && response.status === "partial_failure" && isPositiveInteger(response.merge_id);

const noMatchOutcome = (offer: MergeAmendedSearchOffer | null): MergeOutcome =>
	offer
		? outcome(
				"blocked",
				"warning",
				"No IHI match — amended search available",
				`The Medicare IHI search for the merged record returned no match. ${NOT_MOVED} You can resubmit this search once with amended details.`,
				false,
				offer
		  )
		: outcome(
				"blocked",
				"warning",
				"No IHI match found",
				`The Medicare IHI search for the merged record returned no match. ${NOT_MOVED} An amended search is not available for this attempt.`
		  );

export const describeMergeBlock = (code: unknown, offer: MergeAmendedSearchOffer | null): MergeOutcome => {
	switch (code) {
		case MERGE_CODE_NO_MATCH:
			return noMatchOutcome(offer);
		case "patient_merge_ihi_status_transition_alert_required":
			return blocked(
				"Merge blocked by an HI alert",
				"The HI Service returned an IHI number status for the merged record that needs operator review. An alert was recorded in HI Reviews for the surviving record.",
				true
			);
		case "patient_merge_ihi_unsupported_status":
			return blocked(
				"Merge blocked — unsupported IHI status",
				"The IHI status returned for the merged record is not supported. Check HI Reviews for any alert recorded for the surviving record.",
				true
			);
		case "patient_merge_ihi_different_local_ihis":
			return blocked(
				"Merge blocked — different IHIs",
				"The two records hold different IHIs, which are never resolved automatically. Resolve the IHIs before merging."
			);
		case "patient_merge_ihi_review_unavailable":
			return blocked(
				"Merge blocked — HI review not recorded",
				"A required HI review could not be recorded, so the merge was stopped. Try again later."
			);
		case "patient_merge_ihi_stale_proof_basis":
			return blocked(
				"Merge blocked — records changed",
				"The patient records changed while the IHI was being confirmed. Review the records and start the merge again."
			);
		case "patient_merge_ihi_provider_unavailable":
			return blocked(
				"Merge blocked — HI Service unavailable",
				"The IHI for the merged record could not be confirmed because the HI Service was unavailable. No automatic retry was made."
			);
		case "patient_merge_ihi_multiple_match":
			return blocked(
				"Merge blocked — multiple IHI matches",
				"The HI Service returned more than one possible match, so no single IHI could be confirmed."
			);
		case "patient_merge_ihi_request_rejected":
		case "patient_merge_ihi_indeterminate":
		case "patient_merge_ihi_returned_ihi_differs":
			return NOT_CONFIRMED;
		case "patient_merge_ihi_type_3_criteria_incomplete":
		case "patient_merge_ihi_ihi_inquiry_criteria_incomplete":
			return blocked(
				"Merge blocked — IHI search details incomplete",
				"The merged record does not hold the details the IHI search needs. Complete the patient details and try again."
			);
		case "patient_merge_ihi_one_name_only_type_3_unsupported":
			return blocked(
				"Merge blocked — one-name-only patient",
				"An IHI search for a one-name-only patient is not available in the merge workflow."
			);
		case "patient_merge_ihi_amended_search_not_applicable":
			return blocked(
				"Amended search no longer applies",
				"The records no longer qualify for an amended Medicare IHI search. Start the merge again."
			);
		case null:
			return blocked(
				"Merge blocked by a patient-record check",
				"A patient-record check (such as Medicare, DVA or name details) stopped the merge. Review both records before trying again."
			);
		default:
			return UNKNOWN_MERGE_OUTCOME;
	}
};

// CU-14ygtkj9tu1: null only for an initial request whose failure carries no merge outcome, keeping the existing error notice.
export const classifyMergeFailure = (error: unknown, amended: boolean): MergeOutcome | null => {
	const fallback = amended ? UNCONFIRMED_AMENDMENT : null;
	if (!axios.isAxiosError(error) || !error.response) {
		return fallback;
	}
	const { status } = error.response;
	const body = isRecord(error.response.data) ? error.response.data : {};

	if (status === 409) {
		return body.code === MERGE_CODE_AMENDED_SEARCH_STALE ? STALE_AMENDMENT : UNKNOWN_MERGE_OUTCOME;
	}
	if (status === 422 && body.status === "failed" && isPositiveInteger(body.merge_id)) {
		const block = describeMergeBlock(body.code, parseAmendedSearchOffer(body.amended_search));
		const ihiReviews = parseIhiReviewOutcomes(body.ihi_review_outcomes);
		return ihiReviews.length > 0 ? { ...block, ihiReviews } : block;
	}
	if (amended && status === 422 && isRecord(body.errors) && Object.keys(body.errors).some((key) => key.startsWith("amended_search"))) {
		return INVALID_AMENDMENT;
	}
	return fallback;
};
